import { ClinicianAssignment } from '@/components/bulkQueue/assignment/useClinicianAssignment';
import { DateAssignment } from '@/components/bulkQueue/assignment/useDateAssignment';
import { ServicesAssignment } from '@/components/bulkQueue/assignment/useServicesAssignment';
import { SelectionHeader } from '@/components/bulkQueue/cells/SelectionHeader';
import { ServicesUnifiedVisitSelectionCell } from '@/components/bulkQueue/cells/services/ServicesSelection';
import { getCellHighlightProps } from '@/components/bulkQueue/tables/components';
import { ServicesTableRow } from '@/components/bulkQueue/types';
import {
    getVisitStatusKind,
    visitStatusGlyph,
    visitStatusLabel,
} from '@/components/visits/visitStatusGlyph';
import { existsInExternalSystem } from '@/types/externalId';
import { VisitOutcome, VisitState, VisitWorkflowStep } from '@/types/graphql';
import { renderVisitOutcome, renderVisitState } from '@/types/visit';
import { Box, Tooltip } from '@mui/material';
import { grey } from '@mui/material/colors';

import { InlineOptionalDateRangeDisplay } from '../fulfillmentWindows/FulfillmentWindowDisplay';
import { VisitPriorityIconWithTooltip } from '../icons/PriorityIcon';
import { NoneLabel } from '../labels/NoneLabel';
import { OrderChip } from '../orders/OrderChip';
import { SyncStatusPopover } from '../syncing/SyncStatusPopover';
import { CenteredSortHeadCell, CustomHeadRenderParams } from '../tables/customCells';
import { EditableTextCell } from '../text/EditableTextCell';
import { HorizontalStack, VerticalStack } from '../utils/linearLayouts';
import { VisitRequestActionsCellWithMenu } from '../visitRequests/tables/VisitRequestActionsCell';
import {
    StatusOrOutcomeChip,
    VisitOutcomeChip,
    VisitStateChip,
} from '../visits/StatusAndOutcomeChips';
import { VisitDateTypeDisplay } from '../visits/VisitDateTypeDisplay';
import { VisitWorkflowStepChip } from '../visits/VisitWorkflowStepChip';
import { VisitActionsCell } from '../visits/tables/VisitActionsCell';
import {
    ClinicianAppointmentClinicianCell,
    ClinicianAppointmentServicesCell,
} from '../visits/tables/cells';
import { VisitColumns } from '../visits/tables/columns';
import { AssignmentClinicianVisitCell, AssignmentDateVisitCell } from './AssignmentVisitCells';
import {
    UnifiedVisitOccurrenceRow,
    isUnifiedVisit,
    isUnifiedVisitRequest,
    isVisitRow,
} from './UnifiedVisitOccurrencesTable';

const actionsCellProps = { sx: { width: '44px', paddingLeft: '4px', paddingRight: '4px' } };

export const actionsColumn = {
    // Label blank: the three-dot menu is self-explanatory and header text just
    // costs horizontal space.
    label: '',
    // Right-pinning is opt-in per caller via the table's `pinActions` prop, so
    // the menu stays reachable on a wide, horizontally scrolled table.
    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
        if (isUnifiedVisit(row)) {
            return <VisitActionsCell visitId={row.id} />;
        }
        if (isUnifiedVisitRequest(row)) {
            return <VisitRequestActionsCellWithMenu visitRequest={row} />;
        }
        return <NoneLabel />;
    },
    options: {
        setCellProps: () => actionsCellProps,
        setCellHeaderProps: () => actionsCellProps,
    },
};

export const priorityColumn = {
    field: 'sortPriority',
    // Label blank: the priority icon carries the meaning. A centered,
    // always-visible sort control replaces the header text so the column
    // reads as sortable, and customHeadRender stops mui falling back to
    // rendering the `sortPriority` field name.
    label: '',
    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
        return <VisitPriorityIconWithTooltip priority={row.displayPriority} />;
    },
    options: {
        ...VisitColumns.priorityColumnOptions,
        customHeadRender: (
            columnMeta: CustomHeadRenderParams[0],
            handleToggleColumn: CustomHeadRenderParams[1],
            sortOrder: CustomHeadRenderParams[2],
        ) => (
            <CenteredSortHeadCell
                key={columnMeta.index}
                columnMeta={columnMeta}
                handleToggleColumn={handleToggleColumn}
                sortOrder={sortOrder}
                label='Priority'
            />
        ),
    },
};

const STATE_CELL_PROPS = {
    sx: {
        maxWidth: '150px',
    },
};

export const stateColumn = ({ hasVisitRowSync }: { hasVisitRowSync: boolean }) => ({
    field: 'sortStateIndex',
    label: 'State',
    rowToComponent: (row: UnifiedVisitOccurrenceRow) => (
        <>
            {/* Invisible scroll anchor bc mui table owns the <tr> id.
                getElementById(`visit-<id>`) finds this to scroll the row in. */}
            <span id={`visit-${row.id}`} />
            {isVisitRow(row) ? (
                <HorizontalStack spacing={1} alignItems='center' justifyContent='space-between'>
                    <VerticalStack spacing={1} justifyContent='center'>
                        {row.outcome === VisitOutcome.NONE && (
                            <VisitWorkflowStepChip
                                workflowStep={row.visitWorkflowStep}
                                onlyPending={true}
                            />
                        )}
                        <VisitStateChip state={row.visitState} />
                        {row.outcome !== VisitOutcome.NONE && (
                            <VisitOutcomeChip outcome={row.outcome} />
                        )}
                    </VerticalStack>
                    {hasVisitRowSync && row.syncStatus && (
                        <SyncStatusPopover
                            visitId={row.id}
                            syncStatus={row.syncStatus}
                            existsInExternalSystem={existsInExternalSystem(
                                row.externalId,
                                row.externalIds,
                            )}
                            timezone={row.visitTimezone}
                        />
                    )}
                </HorizontalStack>
            ) : (
                <NoneLabel />
            )}
        </>
    ),
    options: {
        customHeadLabelRender: () => (
            <HorizontalStack width='auto' spacing={1}>
                <Box>State</Box>
            </HorizontalStack>
        ),
        setCellProps: () => STATE_CELL_PROPS,
        sort: true,
    },
});

const centeredCellProps = { sx: { textAlign: 'center' } };

const centeredHeaderProps = {
    sx: { textAlign: 'center !important' },
};

export const syncColumn = {
    label: 'Sync',
    rowToComponent: (row: UnifiedVisitOccurrenceRow) =>
        isVisitRow(row) && row.syncStatus ? (
            <SyncStatusPopover
                visitId={row.id}
                syncStatus={row.syncStatus}
                existsInExternalSystem={existsInExternalSystem(row.externalId, row.externalIds)}
                timezone={row.visitTimezone}
            />
        ) : (
            <NoneLabel />
        ),
    options: {
        setCellProps: () => centeredCellProps,
        setCellHeaderProps: () => centeredHeaderProps,
    },
};

const TERMINAL_STATUS_COLOR = grey[500];
const ATTENTION_STATUS_COLOR = grey[700];

const VisitStatusChip = ({ state, outcome }: { state: VisitState; outcome: VisitOutcome }) => {
    const kind = getVisitStatusKind(state, outcome);
    const label = visitStatusLabel[kind];
    const isTerminal = outcome !== VisitOutcome.NONE;
    const Icon = isTerminal ? visitStatusGlyph[kind] : undefined;
    const color = isTerminal ? TERMINAL_STATUS_COLOR : ATTENTION_STATUS_COLOR;
    const detail = isTerminal
        ? `${renderVisitState(state)} · ${renderVisitOutcome(outcome)}`
        : renderVisitState(state);
    return (
        <Tooltip title={detail}>
            <span>
                <StatusOrOutcomeChip
                    label={label}
                    color={color}
                    icon={
                        Icon ? (
                            <Icon sx={{ fontSize: '14px', color: `${color} !important` }} />
                        ) : undefined
                    }
                    style={isTerminal ? { border: 'none' } : undefined}
                />
            </span>
        </Tooltip>
    );
};

export const statusColumn = {
    field: 'sortStateIndex',
    label: 'Status',
    rowToComponent: (row: UnifiedVisitOccurrenceRow) => (
        <>
            {/* Invisible scroll anchor bc mui table owns the <tr> id.
                getElementById(`visit-<id>`) finds this to scroll the row in. */}
            <span id={`visit-${row.id}`} />
            {isVisitRow(row) ? (
                <VisitStatusChip state={row.visitState} outcome={row.outcome} />
            ) : (
                <NoneLabel />
            )}
        </>
    ),
    options: {
        setCellProps: () => STATE_CELL_PROPS,
        sort: true,
    },
};

export const fulfillmentWindowColumn = {
    field: 'fulfillmentWindow',
    label: 'Fulfillment Window',
    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
        if (isVisitRow(row) && row.visitFulfillmentWindow?.asDateWindow) {
            return (
                <InlineOptionalDateRangeDisplay
                    startDate={row.visitFulfillmentWindow.asDateWindow.startDate}
                    endDate={row.visitFulfillmentWindow.asDateWindow.endDate}
                    timezone={row.visitTimezone}
                    dateProps={{ fontSize: '14px' }}
                />
            );
        }
        return <NoneLabel sx={{ fontSize: '14px' }} />;
    },
};

export const orderColumn = {
    field: 'orderSortKey',
    label: 'Order',
    rowToComponent: (row: UnifiedVisitOccurrenceRow) =>
        row.order ? <OrderChip order={row.order} /> : <NoneLabel sx={{ fontSize: '14px' }} />,
};

export const orderNotesColumn = {
    field: 'order.orderNotes',
    label: 'Order Notes',
    rowToComponent: (row: UnifiedVisitOccurrenceRow) => (
        <Box sx={{ fontSize: '14px' }}>
            <EditableTextCell text={row.order?.orderNotes ?? null} title='Order Notes' />
        </Box>
    ),
};

type ServicesColumnArgs = {
    services: ServicesAssignment;
    occurrenceRows: UnifiedVisitOccurrenceRow[];
    hasBulkSelection: boolean;
    isSingleDiscipline: boolean;
    nextStepInOrder: VisitWorkflowStep;
    needsServiceCount: number;
    loading: boolean;
    showInlineWorkflow: (row: UnifiedVisitOccurrenceRow, step: VisitWorkflowStep) => boolean;
};

export const servicesColumn = ({
    services,
    occurrenceRows,
    hasBulkSelection,
    isSingleDiscipline,
    nextStepInOrder,
    needsServiceCount,
    loading,
    showInlineWorkflow,
}: ServicesColumnArgs) => ({
    field: 'sortServicesName',
    label: 'Services',
    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
        if (isVisitRow(row)) {
            if (showInlineWorkflow(row, VisitWorkflowStep.NEEDS_SERVICE)) {
                return (
                    <ServicesUnifiedVisitSelectionCell
                        visit={row}
                        availableServices={services.availableServicesByVisitId[row.id] ?? []}
                        selectionInfo={services.getSelectionState(
                            row as unknown as ServicesTableRow,
                        )}
                        selectedAppointmentServices={services.selectedAppointmentServices}
                        hasFocusedVisitRequest={services.hasFocusedVisitRequest}
                        enabled={!services.isUpdating && !hasBulkSelection}
                    />
                );
            }
            return <ClinicianAppointmentServicesCell {...row} />;
        }
        return <NoneLabel />;
    },
    options: {
        setCellProps: (_: unknown, rowIndex: number) => {
            const row = occurrenceRows[rowIndex];
            if (showInlineWorkflow(row, VisitWorkflowStep.NEEDS_SERVICE)) {
                const selectionState = services.getSelectionState(
                    row as unknown as ServicesTableRow,
                );
                return {
                    sx: {
                        ...getCellHighlightProps(
                            services.numSelected === 0 || selectionState.isSelected,
                        ),
                    },
                };
            }
            return {};
        },
        customHeadRender: (
            columnMeta: CustomHeadRenderParams[0],
            handleToggleColumn: CustomHeadRenderParams[1],
            sortOrder: CustomHeadRenderParams[2],
        ) => {
            const isDisabled =
                services.isUpdating ||
                services.numSelected === 0 ||
                services.numSelectedAppointmentServices === 0;
            const areAllSelected = services.numSelected === needsServiceCount;
            return (
                <SelectionHeader
                    key={columnMeta.index}
                    columnMeta={columnMeta}
                    handleToggleColumn={handleToggleColumn}
                    sortOrder={sortOrder}
                    hasItems={needsServiceCount > 0}
                    isLoading={loading}
                    isDisabled={isDisabled}
                    isSubmitting={isDisabled}
                    disabled={hasBulkSelection}
                    highlight={nextStepInOrder === VisitWorkflowStep.NEEDS_SERVICE}
                    minWidth={
                        needsServiceCount > 0 ? (isSingleDiscipline ? '230px' : '200px') : undefined
                    }
                    numSelected={services.numSelected}
                    summaryString={services.summaryString}
                    onApply={services.commitAll}
                    selectableCount={isSingleDiscipline ? needsServiceCount : undefined}
                    onSelectAll={
                        isSingleDiscipline
                            ? () => services.toggleSelectAll(areAllSelected)
                            : undefined
                    }
                />
            );
        },
    },
});

type DateColumnArgs = {
    date: DateAssignment;
    occurrenceRows: UnifiedVisitOccurrenceRow[];
    hasBulkSelection: boolean;
    nextStepInOrder: VisitWorkflowStep;
    needsDateCount: number;
    mutationLoading: boolean;
    loading: boolean;
    showInlineWorkflow: (row: UnifiedVisitOccurrenceRow, step: VisitWorkflowStep) => boolean;
};

export const dateColumn = ({
    date,
    occurrenceRows,
    hasBulkSelection,
    nextStepInOrder,
    needsDateCount,
    mutationLoading,
    loading,
    showInlineWorkflow,
}: DateColumnArgs) => ({
    field: 'sortDate',
    label: 'Date',
    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
        if (isVisitRow(row)) {
            if (showInlineWorkflow(row, VisitWorkflowStep.NEEDS_DATE)) {
                return (
                    <AssignmentDateVisitCell
                        visitId={row.id}
                        apply={date.apply}
                        disabled={hasBulkSelection}
                    />
                );
            }
            return (
                <VisitDateTypeDisplay
                    {...row}
                    effectiveDate={null}
                    fulfillmentWindow={null}
                    timezone={row.visitTimezone}
                    fallbackDate={null}
                    label={false}
                    datetimeProps={{ fontSize: '14px' }}
                />
            );
        }
        return <NoneLabel sx={{ fontSize: '14px' }} />;
    },
    options: {
        setCellProps: (_: unknown, rowIndex: number) => {
            const row = occurrenceRows[rowIndex];
            return {
                sx: {
                    ...getCellHighlightProps(
                        showInlineWorkflow(row, VisitWorkflowStep.NEEDS_DATE)
                            ? date.isRowSelected(row.id)
                            : null,
                    ),
                },
            };
        },
        customHeadRender: (
            columnMeta: CustomHeadRenderParams[0],
            handleToggleColumn: CustomHeadRenderParams[1],
            sortOrder: CustomHeadRenderParams[2],
        ) => (
            <SelectionHeader
                key={columnMeta.index}
                columnMeta={columnMeta}
                handleToggleColumn={handleToggleColumn}
                sortOrder={sortOrder}
                hasItems={needsDateCount > 0}
                isLoading={loading || date.headLoading}
                isDisabled={mutationLoading || date.numSelected === 0}
                disabled={hasBulkSelection}
                highlight={nextStepInOrder === VisitWorkflowStep.NEEDS_DATE}
                numSelected={date.numSelected}
                summaryString={date.summaryString}
                onApply={date.commitAll}
                selectableCount={date.selectableCount}
                onSelectAll={date.selectAllCombined}
            />
        ),
    },
});

type ClinicianColumnArgs = {
    clinician: ClinicianAssignment;
    occurrenceRows: UnifiedVisitOccurrenceRow[];
    hasBulkSelection: boolean;
    nextStepInOrder: VisitWorkflowStep;
    needsClinicianCount: number;
    mutationLoading: boolean;
    loading: boolean;
    showInlineWorkflow: (row: UnifiedVisitOccurrenceRow, step: VisitWorkflowStep) => boolean;
};

export const clinicianColumn = ({
    clinician,
    occurrenceRows,
    hasBulkSelection,
    nextStepInOrder,
    needsClinicianCount,
    mutationLoading,
    loading,
    showInlineWorkflow,
}: ClinicianColumnArgs) => ({
    field: 'cliniciansSortKey',
    label: 'Clinician',
    rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
        if (isVisitRow(row)) {
            if (showInlineWorkflow(row, VisitWorkflowStep.NEEDS_CLINICIAN)) {
                return (
                    <AssignmentClinicianVisitCell
                        visitId={row.id}
                        appointments={row.clinicianAppointments}
                        apply={clinician.apply}
                        disabled={hasBulkSelection}
                    />
                );
            }
            return <ClinicianAppointmentClinicianCell {...row} />;
        }
        return <NoneLabel />;
    },
    options: {
        setCellProps: (_: unknown, rowIndex: number) => {
            const row = occurrenceRows[rowIndex];
            if (isVisitRow(row)) {
                return {
                    sx: {
                        ...getCellHighlightProps(
                            showInlineWorkflow(row, VisitWorkflowStep.NEEDS_CLINICIAN)
                                ? clinician.isRowSelected(row.id)
                                : null,
                        ),
                    },
                };
            }
            return {};
        },
        customHeadRender: (
            columnMeta: CustomHeadRenderParams[0],
            handleToggleColumn: CustomHeadRenderParams[1],
            sortOrder: CustomHeadRenderParams[2],
        ) => (
            <SelectionHeader
                key={columnMeta.index}
                columnMeta={columnMeta}
                handleToggleColumn={handleToggleColumn}
                sortOrder={sortOrder}
                hasItems={needsClinicianCount > 0}
                isLoading={loading || clinician.headLoading}
                isDisabled={mutationLoading || clinician.numSelected === 0}
                disabled={hasBulkSelection}
                highlight={nextStepInOrder === VisitWorkflowStep.NEEDS_CLINICIAN}
                numSelected={clinician.numSelected}
                summaryString={clinician.summaryString}
                onApply={clinician.commitAll}
                selectableCount={clinician.selectableCount}
                onSelectAll={clinician.selectAllCombined}
            />
        ),
    },
});

/** Sort key for Order column: order's fulfillment window start date as timestamp (0 if none). */
export const createOrderSortKey = (
    order: { fulfillmentWindow: { startDate: Date | string | null } | null } | null,
) => {
    if (!order?.fulfillmentWindow?.startDate) {
        return 0;
    }
    return new Date(order.fulfillmentWindow.startDate).getTime();
};
